export type PedEmUpInstallOutcome = "installed" | "dismissed" | "manual" | "unavailable";

interface InstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
  prompt(): Promise<void>;
}

export interface PedEmUpPwaState {
  readonly canPrompt: boolean;
  readonly installed: boolean;
  readonly updateAvailable: boolean;
}

type Listener = (state: PedEmUpPwaState) => void;
let deferredPrompt: InstallPromptEvent | null = null;
let waitingWorker: ServiceWorker | null = null;
const listeners = new Set<Listener>();

function runsStandalone() {
  return window.matchMedia("(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui), (display-mode: window-controls-overlay)").matches
    || ("standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
}

function currentState(): PedEmUpPwaState {
  return {
    canPrompt: deferredPrompt !== null,
    installed: runsStandalone() || window.localStorage.getItem("pedemup:pwa-installed") === "true",
    updateAvailable: waitingWorker !== null,
  };
}

function publish() {
  const state = currentState();
  listeners.forEach((listener) => listener(state));
}

export function subscribeToPedEmUpPwa(listener: Listener) {
  listeners.add(listener);
  listener(currentState());
  return () => { listeners.delete(listener); };
}

export async function requestPedEmUpInstall(): Promise<PedEmUpInstallOutcome> {
  if (currentState().installed) return "installed";
  if (!deferredPrompt) return "manual";
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  publish();
  return choice.outcome === "accepted" ? "installed" : "dismissed";
}

export function activatePedEmUpUpdate() {
  waitingWorker?.postMessage({ type: "SKIP_WAITING" });
}

export function registerPedEmUpPwa() {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    publish();
  });
  window.addEventListener("appinstalled", () => {
    window.localStorage.setItem("pedemup:pwa-installed", "true");
    deferredPrompt = null;
    publish();
  });
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").then((registration) => {
      if (registration.waiting) {
        waitingWorker = registration.waiting;
        publish();
      }
      registration.addEventListener("updatefound", () => {
        const worker = registration.installing;
        worker?.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            waitingWorker = worker;
            publish();
          }
        });
      });
    }).catch(() => undefined);
  });
  navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload());
}
